import { ServerResponse, WithAuditDetails } from 'data/types';
import { AxiosError } from 'axios';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import * as api from './cspConfig.api';
import { extractErrorMessage } from '../../utils';
import {
  GET_CSP_CONFIG_LIST,
  GET_CSP_CONFIG,
  CREATE_CSP_CONFIG,
  UPDATE_CSP_CONFIG,
  DELETE_CSP_CONFIG,
  CspConfigActionTypes,
  CspConfig,
  GO_TO_CSP_CONFIG_LIST,
  SELECT_CSP_CONFIG,
  SelectCspConfigAction,
  DeleteCspConfigThunkAction,
  UpdateCspConfigThunkAction,
  CreateCspConfigThunkAction,
  OPEN_CSP_CONFIG_CREATE_PAGE,
  CLOSE_CSP_CONFIG_CREATE_PAGE,
  OPEN_CSP_CONFIG_EDIT_PAGE,
  CLOSE_CSP_CONFIG_EDIT_PAGE,
  SET_CSP_CONFIG_LOADING,
  GO_TO_CSP_CONFIG_VIEW,
  SetCspConfigLoadingAction,
} from './cspConfig.types';
import {
  CSP_CONFIG_ROUTE, SYSTEM, SYSTEM_CONFIGURATION_ROUTE, TENANT_ROUTE,
} from '../../constants';
import { getSelectedCspConfig } from './cspConfig.selectors';

/**
 * The CSP pages live under System Configuration and under People > Tenant; navigation
 * stays in whichever section the user is in.
 */
export function getCspConfigBasePath(history?: History): string {
  const pathname = history?.location?.pathname ?? '';
  if (pathname === TENANT_ROUTE || pathname.startsWith(`${TENANT_ROUTE}/`)) {
    return TENANT_ROUTE;
  }
  return `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CSP_CONFIG_ROUTE}`;
}

export function goToCspConfigList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_CSP_CONFIG_LIST,
    });

    history.push(`${getCspConfigBasePath(history)}`);
  };
}

export function goToCspConfigCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_CSP_CONFIG_CREATE_PAGE,
    });

    history.push(`${getCspConfigBasePath(history)}/create`);
  };
}

export function closeCspConfigCreatePage(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_CSP_CONFIG_CREATE_PAGE,
    });

    history.push(`${getCspConfigBasePath(history)}`);
  };
}

export function goToCspConfigEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_CSP_CONFIG_EDIT_PAGE,
    });

    history.push(`${getCspConfigBasePath(history)}/edit`);
  };
}

export function goToCspConfigView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_CSP_CONFIG_VIEW,
    });

    history.push(`${getCspConfigBasePath(history)}/view`);
  };
}

export function closeCspConfigEditPage(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_CSP_CONFIG_EDIT_PAGE,
    });

    history.push(`${getCspConfigBasePath(history)}`);
  };
}

export function setCspConfigLoadingAction(isLoading: boolean): SetCspConfigLoadingAction {
  return {
    type: SET_CSP_CONFIG_LOADING,
    payload: { isLoading },
  };
}

export function getCspConfigList(deliveryFunctionId: string): CspConfigActionTypes {
  return {
    type: GET_CSP_CONFIG_LIST,
    payload: api.getCspConfigList(deliveryFunctionId),
  };
}

export function getCspConfig(deliveryFunctionId: string, id: string): CspConfigActionTypes {
  return {
    type: GET_CSP_CONFIG,
    payload: api.getCspConfig(deliveryFunctionId, id),
  };
}

export function createCspConfig(
  deliveryFunctionId: string,
  body: WithAuditDetails<CspConfig>,
  history: History,
): CreateCspConfigThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_CSP_CONFIG,
        payload: api.createCspConfig(deliveryFunctionId, body),
      });

      dispatch(getCspConfigList(deliveryFunctionId));
      dispatch(goToCspConfigList(history));
      showSuccessMessage(i18n.t('cspConfig.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('cspConfig.create.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function updateCspConfig(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<CspConfig>,
  history: History,
): UpdateCspConfigThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_CSP_CONFIG,
        payload: api.updateCspConfig(deliveryFunctionId, id, body),
      });

      dispatch(getCspConfigList(deliveryFunctionId));
      dispatch(goToCspConfigList(history));
      showSuccessMessage(i18n.t('cspConfig.edit.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('cspConfig.edit.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function deleteCspConfig(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteCspConfigThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedCspConfig] = getSelectedCspConfig(getState());

    try {
      await dispatch({
        type: DELETE_CSP_CONFIG,
        payload: api.deleteCspConfig(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('cspConfig.delete.success', { name: selectedCspConfig?.name }), 5000);
      dispatch(getCspConfigList(deliveryFunctionId));
      if (history != null) {
        dispatch(goToCspConfigList(history));
      }
    } catch (e) {
      showError(i18n.t('cspConfig.delete.failed', { name: selectedCspConfig?.name }));
    }
  };
}

export function selectCspConfig(
  cspConfig: CspConfig[],
): SelectCspConfigAction {
  return {
    type: SELECT_CSP_CONFIG,
    payload: cspConfig,
  };
}
